import {
    useStyles,
    SearchBar as AriaSearchBar,
    useAppDisplayModeContext,
    pxArrayToRem,
    Button,
    pxToRem,
} from '@archipro-design/aria';

import { SearchLine, CloseLine } from '@archipro-design/icons';
import { Search as SearchIcon, X as CloseIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useBoolean, useDebounce } from 'ahooks';
import { cn } from '@rocco/utils/cn';
import * as S from './InPageSearchBar.style';
import { AnimatePresence, motion } from 'framer-motion';

const SEARCH_MIN_CHARS = 2;

interface SearchBarProps {
    hidden?: boolean;
    onSearchChange: (keyword: string) => void;
    onSearchClick?: (keyword: string) => void;
    defaultKeyword?: string;
    toggleSearchDrawer?: () => void;
    minChars?: number;
    isSearchOnSubmit?: boolean;
    preventAutoFocus?: boolean;
    /**
     * `revamp` renders the BC-4505 detail-page pill (Figma 2354:58811) — grey
     * search icon + input + a circular search button — instead of the legacy
     * aria search bar. `theme` flips it for the dark Projects tab.
     */
    variant?: 'default' | 'revamp';
    theme?: 'light' | 'dark';
    placeholder?: string;
}

export const InPageSearchBar: React.FC<SearchBarProps> = ({
    hidden = false,
    defaultKeyword = '',
    onSearchChange,
    onSearchClick,
    toggleSearchDrawer,
    minChars = SEARCH_MIN_CHARS,
    isSearchOnSubmit = false,
    preventAutoFocus,
    variant = 'default',
    theme = 'light',
    placeholder = 'Search',
}) => {
    const [keyword, setKeyword] = useState(defaultKeyword);
    const { desktop } = useAppDisplayModeContext();
    const inputRef = useRef<HTMLInputElement>(null);
    const [focus, { set: setFocus }] = useBoolean(
        !!inputRef.current?.value?.length,
    );

    const { css } = useStyles({
        isEmptyKeyword: keyword === '',
        isFocus: focus,
    });

    const debouncedKeyword = useDebounce(keyword, { wait: 600 });
    const previousKeywordRef = useRef(debouncedKeyword);

    useEffect(() => {
        if (!preventAutoFocus && inputRef.current?.value?.length) {
            inputRef.current.focus();
        }
    }, [preventAutoFocus]);

    useEffect(() => {
        if (
            previousKeywordRef.current !== debouncedKeyword &&
            debouncedKeyword.length >= minChars
        ) {
            if (!isSearchOnSubmit) onSearchChange(debouncedKeyword);
            previousKeywordRef.current = debouncedKeyword;
        }
    }, [debouncedKeyword, onSearchChange, minChars, isSearchOnSubmit]);

    const onChangeKeyword = (value: string) => {
        setKeyword(value);
    };

    const onClose = () => {
        setKeyword('');
        onSearchChange('');
        if (!desktop) {
            inputRef.current?.focus();
        }
    };

    const searchIconSize = desktop ? 24 : 24;

    if (variant === 'revamp') {
        const isDark = theme === 'dark';
        const onButtonClick = () => {
            if (keyword) onClose();
            inputRef.current?.focus();
        };
        return hidden ? null : (
            <div
                className={cn(
                    // Desktop is sized to the 42px (h-10.5) control row it sits
                    // in (Hide Filters / Display / Sort) rather than the taller
                    // mobile pill; min-w-0 lets it shrink below 24rem when the
                    // filter sidebar squeezes the row.
                    'flex w-full items-center justify-between rounded-full py-[0.5625rem] pr-[0.5625rem] pl-5 md:w-[24rem] md:min-w-0 md:py-[0.3125rem] md:pr-[0.3125rem] md:pl-[1.25rem]',
                    isDark ? 'bg-white/[0.08]' : 'bg-[#f6f6f6]',
                )}
            >
                <div className="flex min-w-0 flex-1 items-center gap-[0.6875rem]">
                    <SearchIcon
                        aria-hidden="true"
                        className={cn(
                            'size-[1.29rem] shrink-0 md:size-[1.125rem]',
                            isDark ? 'text-white' : 'text-[#1f1f21]',
                        )}
                    />
                    <input
                        ref={inputRef}
                        type="search"
                        value={keyword}
                        placeholder={placeholder}
                        onChange={(e) => onChangeKeyword(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.currentTarget.blur();
                                if (isSearchOnSubmit)
                                    onSearchChange(debouncedKeyword);
                            }
                        }}
                        onFocus={() => setFocus(true)}
                        onBlur={() => setFocus(false)}
                        className={cn(
                            // Mobile is a literal 16px: this config's text-base
                            // resolves under 16, and any input below 16px makes
                            // iOS Safari zoom the page on focus.
                            'w-full min-w-0 border-0 bg-transparent leading-none outline-none max-md:text-[16px] md:text-[1rem] md:tracking-[-0.025rem] [&::-webkit-search-cancel-button]:appearance-none',
                            isDark
                                ? 'text-white placeholder:text-white/40'
                                : 'text-[#1f1f21] placeholder:text-[#1f1f21]/40',
                        )}
                    />
                </div>
                <button
                    type="button"
                    aria-label={keyword ? 'Clear search' : 'Search'}
                    onClick={onButtonClick}
                    className={cn(
                        'flex size-[2.5rem] shrink-0 items-center justify-center rounded-full transition-colors md:size-8',
                        // Dark keeps a white icon (matches the page's white-on-dark
                        // treatment) on a lighter circle so it stays visible.
                        isDark
                            ? 'bg-white/[0.14] text-white hover:bg-white/20'
                            : 'bg-[#181818] text-white hover:bg-[#2d2d2d]',
                    )}
                >
                    {keyword ? (
                        <CloseIcon className="size-[1.05rem]" />
                    ) : (
                        <SearchIcon className="size-[1.05rem]" />
                    )}
                </button>
            </div>
        );
    }

    return hidden ? null : (
        <div className={css(desktop ? S.SearchBarDesktop : S.SearchBarMobile)}>
            <AriaSearchBar
                iconButtonAfter={{
                    onClick: () => {
                        if (keyword) {
                            onClose();
                        }
                        inputRef.current?.focus();
                    },
                    size: searchIconSize,
                    padding: 0,
                    icon: keyword ? (
                        <CloseLine />
                    ) : !focus ? (
                        <SearchLine />
                    ) : undefined,
                }}
                iconButtonBefore={undefined}
                searchInput={{
                    type: 'search',
                    input: {
                        value: keyword,
                        autoFocus: !desktop && !keyword && true,
                        ref: inputRef,
                    },
                    onChange: (e: React.SyntheticEvent) =>
                        onChangeKeyword((e.target as HTMLInputElement).value),
                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                            e.currentTarget.blur();
                            if (isSearchOnSubmit)
                                onSearchChange(debouncedKeyword);
                        }
                    },
                    onClick: (e: React.SyntheticEvent) =>
                        onSearchClick &&
                        onSearchClick((e.target as HTMLInputElement).value),
                    onFocus: () => setFocus(true),
                    onBlur: () => setFocus(false),
                }}
                variables={{
                    searchBarPadding: !desktop
                        ? pxArrayToRem([14, 8])
                        : undefined,
                    searchInputPaddingX: !desktop ? '0' : undefined,
                    searchBarBorderRadius: !desktop ? '4px' : undefined,
                }}
            />
            {desktop && <div className={'animated-underline'}></div>}
            {!desktop && (
                <AnimatePresence>
                    {focus && (
                        <motion.div
                            style={{ overflow: 'hidden' }}
                            initial={{ width: 0 }}
                            animate={{ width: 'auto' }}
                            exit={{ width: 0 }}
                        >
                            <Button
                                variables={() => ({
                                    buttonPadding: pxToRem(9),
                                })}
                                size={14}
                                transparent
                                className={css(S.MobileButtonCancel)}
                                onClick={() => {
                                    toggleSearchDrawer?.();
                                }}
                            >
                                Cancel
                            </Button>
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
        </div>
    );
};
